import { AfterContentInit, ContentChild, ContentChildren, forwardRef, Input, Output, QueryList, TemplateRef, EventEmitter, Directive, OnInit, OnDestroy, Inject, ElementRef } from '@angular/core';
import { FocusKeyManager, FocusMonitor, FocusableOption, ListKeyManager } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { SelectionModel } from '@angular/cdk/collections';
import { PhxListItemTextLineDirective } from '../list-item-text-line.directive';
import { compareWith } from '../../../utils';
import mixinDisabled from '../../../mixins/disabled';

type SelectionType = 'single' | 'multiple'| undefined;

type SelectedValueType = string | string[];

const baseOptionMixin = mixinDisabled(
  class {
    constructor() {}
  }
);
@Directive()
export abstract class BaseListComponent implements AfterContentInit {

  @Input() public listData?: any[];
  @Input()
  get selection(): SelectionType {
    if (!this.selectedOptions) {
      return;
    }
    return this.selectedOptions.isMultipleSelection() ? 'multiple' : 'single';
  }
  set selection(value: SelectionType){
    if (value) {
      this.selectedOptions = new SelectionModel(value === 'multiple' ? true : false);
    }
  }

  @Input()
  get selectedValue(): SelectedValueType | undefined {
    return this._selectedValue;
  }
  set selectedValue(value: string | string[]) {
    this._selectedValue = value;
    this.selectedValueChange.emit(this._selectedValue);
  }

  @Output() public selectedValueChange = new EventEmitter();
  @ContentChildren(forwardRef(() => BaseListItemComponent), {descendants: true}) protected listItems?: QueryList<BaseListItemComponent>;
  @ContentChild(TemplateRef) public listItemTemplate: TemplateRef<any>;

  public selectedOptions?: SelectionModel<BaseListItemComponent>;
  protected keyManager: ListKeyManager<BaseListItemComponent>;
  private _selectedValue?: SelectedValueType;

  public get multiple(): boolean {
    return this.selection === 'multiple';
  }

  public get single(): boolean {
    return this.selection === 'single';
  }

  public get selectable(): boolean {
    return coerceBooleanProperty(this.selection);
  }

  public valueChanged() {
    if (this.listItems && this.selectedValue) {
      const values = this.listItems?.filter(listItem => listItem.selected).map(listItem => listItem.value);
      this.selectedValue = values;
    }
  }

  public onKeydown(event: KeyboardEvent): void {
    this.keyManager.onKeydown(event);
  }

  public onFocus() {
    if (this.keyManager.activeItemIndex !== -1) {
      this.keyManager.setActiveItem(this.keyManager.activeItemIndex);
    } else {
      this.keyManager.setFirstItemActive();
    }
  }

  ngAfterContentInit(): void {
    this.keyManager = new FocusKeyManager<BaseListItemComponent>(this.listItems).withWrap();
    this.keyManager.setFirstItemActive();
  }

}

@Directive()
export abstract class BaseListItemComponent extends baseOptionMixin implements OnInit, OnDestroy, FocusableOption {

  @Input() checkboxPosition: 'before' | 'after' = 'before';
  @Input() value?: string;
  @Input() public indeterminate: boolean = false;
  @Output() readonly toggleSelection: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ContentChildren(PhxListItemTextLineDirective) textItems: QueryList<PhxListItemTextLineDirective>;
  listItemIntialized = false;

  @Input()
  get selected(): boolean {
    return (this.selectionList?.selectable && this.selectionList?.selectedOptions?.isSelected(this)) ?? false;
  }
  set selected(isSelected: boolean) {
    this.setSelected(isSelected);
    this.selectionList?.valueChanged();
  }
  public phxCondensed: boolean = false;
  private _selected = false;
  private listItemInitialized = false;

  protected setSelected(isSelected : boolean){
    if (this.selectionList?.selectable && isSelected !== this._selected) {

      this._selected = isSelected;

      if (isSelected) {
        this.selectionList?.selectedOptions?.select(this);
      } else {
        this.selectionList?.selectedOptions?.deselect(this);
      }
    }
  }

  public focus(): void {
    this.elementRef.nativeElement?.focus();
  }

  public getLabel?(): string {
    return this.elementRef.nativeElement?.textContent ?? '';
  }

  public toggleSelected(){
    this.indeterminate = false;
    if (this.selectionList.selectable){
      this.selected = !this.selected;
      this.toggleSelection.emit(this.selected);
    }
  }

  public handleCheckClick(event) {
    event.preventDefault();
  }

  constructor(
    @Inject(forwardRef(() => BaseListComponent)) public selectionList: BaseListComponent,
    protected elementRef: ElementRef,
    protected focusMonitor: FocusMonitor) {
      super();
    }

  ngOnInit() {
    const list = this.selectionList;
    if (list?.selectedValue && Array.from(list?.selectedValue).some(value => compareWith(this.value, value))) {
      this.setSelected(true);
    }
    this.listItemInitialized = true;

    this.focusMonitor.monitor(this.elementRef).subscribe();
  }

  ngOnDestroy(){
    this.focusMonitor.stopMonitoring(this.elementRef);
  }
}

